import { Autocomplete, FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, TextField, ThemeProvider } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import theme from "../style/theme";
import { useTranslation } from "react-i18next";

export default function FormTextField({ id, label, regex, type, errorMsg, adornment, adornmentPosition, numberMin, numberMax }) {
    const [error, setError] = useState(false);

    return <FormControl error={error} fullWidth sx={{ m: 1 }} color="primary">
        <InputLabel htmlFor={id}>{label}</InputLabel>
        <OutlinedInput
            id={id}
            startAdornment={<InputAdornment position={adornmentPosition}>{adornment}</InputAdornment>}
            label={label}
            type=""
            onChange={(val) => {
                if (val.target.value.length !== 0 && (val.target.value.match(regex) === null || val.target.value.match(regex).length !== 1 || val.target.value.match(regex)[0].length === 0)) {
                    setError(true);
                } else {
                    if (type === 'number' && ((numberMin !== null && parseFloat(val.target.value) < numberMin) || (numberMax !== null && parseFloat(val.target.value) > numberMax))) {
                        setError(true);
                    }else {
                        setError(false);
                    }
                }
            }}
        />
        {error ? <FormHelperText id={`${id}-helper-text`}>{errorMsg.split('\n').map((line, index) => <div key={index}>{line}</div>)}</FormHelperText> : null}
    </FormControl>;
}

export function EditableText({ id, text='', onChange=(value)=>{}, onSave=(value)=>{}, onEnd=()=>{}, onStartEditing=()=>{return true;}, canEdit=true }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(text);
    const inputRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        console.log('Text set to:', text);
        setEditedText(text);
    }, [text]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(0, inputRef.current.value.length);
        }
    }, [isEditing]);

    const handleClick = () => {
        if (onStartEditing()) {
            setIsEditing(true);
        }
    };

    const handleChange = (e) => {
        setEditedText(e.target.value);
        onChange(e.target.value);
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
            saveChanges();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }

        if (e.key === 'Enter' || e.key === 'Escape') {
            onEnd();
        }
    };

    const saveChanges = () => {
        if (editedText.trim() !== text) {
            onSave(editedText.trim());
        }
        setIsEditing(false);
    };

    const cancelEditing = () => {
        setEditedText(text);
        setIsEditing(false);
    };

    return <div id={id} className={canEdit?'editable-text':''}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editedText}
          onChange={handleChange}
          className="editable-input"
          onKeyDown={handleKeyDown}
        />
      ) : (
        <div
            ref={textRef}
            onClick={canEdit ? handleClick : null}
        >
            {editedText}
        </div>
      )}
    </div>;
}

export function MultilingualEditableText({ id, texts={ca:'', es:'', en:''}, languages=['ca', 'es', 'en'], onChange=(value, language)=>{}, onSave=(value, language)=>{}, onEnd=()=>{}, onStartEditing=()=>{return true;}, displayLanguage='en', tag='p', className='', customTheme=theme, canEdit=true, style={}, sx={}, bottomWidget=null }) {
    const { t } = useTranslation();
    
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(texts);
    const [language, setLanguage] = useState(displayLanguage);
    const inputRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        setEditedText(texts);
    }, [texts]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(0, inputRef.current.value.length);
        }
    }, [isEditing, language]);

    const handleClick = () => {
        if (onStartEditing()) setIsEditing(true);
    };

    const handleChange = (e) => {
        const newValue = e.target.value;
        setEditedText(prev => ({
            ...prev,
            [language]: newValue
        }));
        onChange(e.target.value, language);
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
            saveChanges();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }

        if (e.key === 'Enter' || e.key === 'Escape') {
            onEnd();
        }
    };

    const saveChanges = () => {
        var save = false;
        for (const lang of languages) {
            if (editedText[lang].trim() !== texts[lang]) {
                save = true;
            }
        }
        if (save) {
            onSave(editedText, language);
        }
        setIsEditing(false);
    };

    const cancelEditing = () => {
        setEditedText(texts);
        setIsEditing(false);
    };

    return <div id={id} className={`${canEdit?'editable-text':''} ${className}`} style={{width: '100%', ...sx}}>
        {isEditing
            ? <ThemeProvider theme={customTheme}>
                <Autocomplete
                    value={language}
                    disablePortal
                    options={languages}
                    size="small"
                    sx={{ width: 300, maxWidth: '100%', margin: '5px 0px', fontStyle: 'normal', color: 'black', 'svg': {
                        color: 'black',
                        width: '20px',
                        height: '20px',
                    } }}
                    style={{fontStyle: 'normal', color: 'black'}}
                    onChange={(event, newValue) => {
                        if (languages.includes(newValue)) setLanguage(newValue);
                    }}
                    color="info"
                    renderInput={(params) => <TextField {...params} label={t('widget.languageSwitcher')} />}
                />
            </ThemeProvider> : null
        }
        {isEditing ? (
            <textarea
                ref={inputRef}
                type="text"
                value={editedText[language]}
                onChange={handleChange}
                className="editable-input"
                onKeyDown={handleKeyDown}
                style={{width: '100%', resize: 'vertical', ...style}}
            />
        ) : (
            <div
                ref={textRef}
                onClick={canEdit ? handleClick : null}
            >
                {
                    tag === 'p'
                        ? <p className={className}>{editedText[displayLanguage]}</p>
                        : tag === 'div'
                            ? editedText[displayLanguage]
                            : tag === 'h3'
                                ? <h3>{editedText[displayLanguage]}</h3>
                                : tag === 'span'
                                    ? <span>{editedText[displayLanguage]}</span>
                                    : tag === 'li'
                                        ? <li>{editedText[displayLanguage]}</li>
                                        : tag === 'ul'
                                            ? <ul>{editedText[displayLanguage]}</ul>
                                            : tag === 'ol'
                                                ? <ol>{editedText[displayLanguage]}</ol>
                                                : tag === 'h2'
                                                    ? <h2>{editedText[displayLanguage]}</h2>
                                                    : null
                }
            </div>
        )}
        {isEditing ? bottomWidget : null}
    </div>;
}